<template>
  <NCollapse class="c-contact-selector-accounts-with-contacts" :default-expanded-names="['customers']">
    <NCollapseItem
      class="c-contact-selector-accounts-collapse-item h-full"
      :disabled="mutuallyExclusiveQuickFilters"
      name="customers"
    >
      <template v-if="search.length === 0" #arrow>
        <CIcon faType="fal" icon="clock" :size="12" />
      </template>
      <template v-if="search.length > 0" #header>
        <NH6 class="ma-0">
          {{ `${t('canvasUI.CContactSelectorAccountsWithContacts.customers')} (${totalAccountsCount})` }}
        </NH6>
      </template>
      <template v-else #header>
        <NH6 class="ma-0"> {{ t('canvasUI.CContactSelectorAccountsWithContacts.recents') }} </NH6>
      </template>
      <DynamicScroller
        v-if="search.length > 0"
        class="c-contact-selector-accounts-with-contacts__account-scroller"
        :items="accountsWithContacts"
        keyField="Id"
        :min-item-size="70"
      >
        <template v-slot="{ item: account, index, active }">
          <DynamicScrollerItem
            :active="active"
            :data-index="index"
            :item="account"
            :page="false"
            :size-dependencies="[account.contacts?.length, getAccountSecondLine(account)]"
          >
            <CContactAccountListItem
              :accountId="account.Id"
              :address="getAccountSecondLine(account)"
              :allowAccountChange="allowAccountChange"
              :allowContactsFromDifferentAccounts="allowContactsFromDifferentAccounts"
              :cardIcon="search.length === 0 ? 'address-card' : ''"
              :contacts="account.contacts"
              :customFieldValue="
                customAccountDisplayField && account[customAccountDisplayField]
                  ? account[customAccountDisplayField]
                  : undefined
              "
              :date-format="dateFormat"
              :last-viewed-date="search.length === 0 ? account?.LastViewedDate : ''"
              :loading="loading"
              :name="account.Name"
              :restrict-to-single-contact="restrictToSingleContact"
              :search="search"
              :selected-account-ids="selectedAccountIds"
              :selected-contact-ids="selectedContactIds"
              @add-account="$emit('add-account', $event)"
              @add-contact="$emit('add-contact', $event)"
              @remove-account="$emit('remove-account', $event)"
              @remove-contact="$emit('remove-contact', $event)"
            />
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>
      <div v-else class="c-contact-selector-accounts-with-contacts__account-scroller">
        <CContactAccountListItem
          v-for="account in accountsWithContacts"
          :key="account.Id"
          :accountId="account.Id"
          :address="getAccountSecondLine(account)"
          :allowAccountChange="allowAccountChange"
          :allowContactsFromDifferentAccounts="allowContactsFromDifferentAccounts"
          cardIcon="address-card"
          :contacts="account.contacts"
          :customFieldValue="
            customAccountDisplayField && account[customAccountDisplayField]
              ? account[customAccountDisplayField]
              : undefined
          "
          :date-format="dateFormat"
          :last-viewed-date="account?.LastViewedDate"
          :loading="loading"
          :name="account.Name"
          :restrict-to-single-contact="restrictToSingleContact"
          :search="''"
          :selected-account-ids="selectedAccountIds"
          :selected-contact-ids="selectedContactIds"
          @add-account="$emit('add-account', $event)"
          @add-contact="$emit('add-contact', $event)"
          @remove-account="$emit('remove-account', $event)"
          @remove-contact="$emit('remove-contact', $event)"
        />
      </div>
    </NCollapseItem>
  </NCollapse>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { NCollapse, NCollapseItem, NH6 } from 'naive-ui'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import { orderBy, uniqBy } from 'lodash-es'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CContactAccountListItem from '@lib/components/CContactSelector/CContactAccountListItem.vue'

export default defineComponent({
  name: 'CContactSelectorAccountsWithContacts',
  props: {
    mutuallyExclusiveQuickFilters: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    loading: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    allowAccountChange: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    allowContactsFromDifferentAccounts: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    restrictToSingleContact: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    search: {
      type: String as PropType<string>,
      required: true,
      default: '',
    },
    selectedAccountIds: {
      type: Array as PropType<string[]>,
      required: true,
      default: () => [],
    },
    selectedContactIds: {
      type: Array as PropType<string[]>,
      required: true,
      default: () => [],
    },
    singleColumnView: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    totalAccountsCount: {
      type: Number as PropType<number>,
      required: true,
    },
    accounts: {
      type: Array as PropType<any[]>,
      required: true,
    },
    contacts: {
      type: Array as PropType<any[]>,
      required: true,
    },
    customAccountDisplayField: {
      type: String as PropType<string>,
      default: undefined,
    },
    dateFormat: {
      type: String as PropType<string>,
      default: 'DD/MM/YYYY',
    },
    getAccountSecondLine: {
      type: Function as PropType<(account: any) => string>,
      default: (account: any) => account.BillingAddress?.street || '',
    },
  },
  emits: ['add-account', 'add-contact', 'remove-account', 'remove-contact'],
  components: {
    DynamicScroller,
    DynamicScrollerItem,
    NCollapse,
    NCollapseItem,
    NH6,
    CContactAccountListItem,
    CIcon,
  },
  setup(props) {
    const { t } = useI18n()

    const nameIteratee = (i: any) => i?.Name?.toLowerCase()
    const accountsWithContacts = computed(() => {
      const contactsList = orderBy(
        uniqBy(props.accounts, 'Id').map((account) => ({
          ...account,
          contacts: orderBy(
            uniqBy(
              props.contacts.filter((contact) => contact.AccountId === account.Id),
              'Id',
            ),
            [nameIteratee],
            ['asc'],
          ),
        })),
        [nameIteratee],
        ['asc'],
      )
      if (props.search) {
        return contactsList
      }
      return contactsList.slice(0, 5)
    })

    return {
      accountsWithContacts,
      t,
    }
  },
})
</script>

<style scoped>
:deep(
  .c-contact-selector-accounts-collapse-item .n-collapse-item__header.n-collapse-item__header.n-collapse-item__header
) {
  cursor: auto;
}

.c-contact-selector-accounts-with-contacts {
  height: 100%;
}
.c-contact-selector-accounts-with-contacts__account-scroller {
  height: 100%;
  overflow-y: auto;
  min-height: v-bind('$props.singleColumnView ? "30svh" : "40svh"');
  max-height: v-bind('$props.singleColumnView ? "calc(100svh - 400px)" : "40svh"');
}
</style>
